/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React from 'react';
import { Typography } from '@mui/material';

interface Props {
	person?: { firstName?: string; lastName?: string; deleted?: boolean };
	textVariant: 'body1' | 'body2' | 'h6';
}

/**
 * Return the complete persona name.
 * Case 1: Returns 'Unbekannt' if person is undefined
 * Case 2: Returns 'Gelöschte Person' if person is deleted
 * Case 3: Returns 'Firstname Lastname' if person is not undefined
 * @param person
 */
export const getCompletePersonName = (person?: {
	firstName?: string;
	lastName?: string;
	deleted?: boolean;
}): string => {
	let completeName = '';
	if (!person) {
		completeName = 'Unbekannt';
	} else if (person?.deleted) {
		completeName = 'Gelöschte Person';
	} else {
		if (person.firstName && person.lastName) {
			completeName = `${person.firstName} ${person.lastName}`;
		} else if (!person.firstName && person.lastName) {
			completeName = `${person.lastName}`;
		} else if (person.firstName && !person.lastName) {
			completeName = `${person.lastName}`;
		}
	}
	return completeName;
};

const PersonNameView = (props: Props): JSX.Element => {
	const { person, textVariant } = props;
	return (
		<Typography variant={textVariant} color="primary">
			{getCompletePersonName(person)}
		</Typography>
	);
};

export default PersonNameView;
